import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Box, Column, ConfirmationModal, Typography, useOverlayContext } from 'seagull';
import { ContactAccountingMapping } from '~/components/ContactForm/ContactAccountingMapping';
import { ContactForm } from '~/components/ContactForm/ContactForm';
import { normalizeContactPhone } from '~/components/ContactForm/ContactForm.utils';
import { useHasLinkedAccounting } from '~/components/ContactForm/hooks/useHasLinkedAccounting';
import { useCreateClientContact } from '~/hooks/reactQuery/queries/useClientMutations';
import { useApplyAccountingMapping } from '~/hooks/useApplyAccountingMapping';
import { useLocaleKeys } from '~/i18n';
import { addContactModalTextsPath, ADD_CONTACT_FORM_DEFAULTS, CONTACT_FIELD_NAME, CONTACT_FORM_TEST_ID, GetAddContactModalTID } from './AddContactModal.utils';
const ADD_CONTACT_SUCCESS_TOAST_TEST_ID = 'add-contact-success-toast';
export function AddContactModal(param) {
    let { clientId, dismissModal, testId } = param;
    const { t } = useLocaleKeys(addContactModalTextsPath);
    const { AddContactModalTestIds } = useMemo(()=>GetAddContactModalTID(testId), [
        testId
    ]);
    const { createClientContact } = useCreateClientContact();
    const applyAccountingMapping = useApplyAccountingMapping();
    const { showSuccessToast } = useOverlayContext();
    const hasLinkedAccounting = useHasLinkedAccounting();
    const [hasError, setHasError] = useState(false);
    const methods = useForm({
        mode: 'all',
        defaultValues: ADD_CONTACT_FORM_DEFAULTS
    });
    const onValid = async (param)=>{
        let { contact } = param;
        setHasError(false);
        let contactId;
        let version;
        try {
            ({ contactId, version } = await createClientContact({
                clientId,
                firstName: contact.firstName,
                lastName: contact.lastName,
                email: contact.email,
                phone: normalizeContactPhone(contact.phone)
            }));
        } catch (unused) {
            setHasError(true);
            return;
        }
        const mappingSuccessful = await applyAccountingMapping(contactId, version, {
            qbo: contact.qbo,
            xero: contact.xero
        }, {});
        if (mappingSuccessful) showSuccessToast(t.contactAdded(), ADD_CONTACT_SUCCESS_TOAST_TEST_ID);
        dismissModal();
    };
    const modalProps = {
        title: t.title(),
        primaryButtonLabel: t.saveButton(),
        secondaryButtonLabel: t.cancelButton()
    };
    return /*#__PURE__*/ _jsx(FormProvider, _object_spread_props(_object_spread({}, methods), {
        children: /*#__PURE__*/ _jsx(ConfirmationModal, _object_spread_props(_object_spread({}, modalProps), {
            testId: AddContactModalTestIds.Root(),
            textAlignCenter: false,
            bi: {
                primaryButton: {
                    name: 'add_contact_submit'
                },
                secondaryButton: {
                    name: 'add_contact_cancel'
                },
                closeButton: {
                    name: 'add_contact_close'
                }
            },
            primaryButtonTestId: AddContactModalTestIds.SaveButton(),
            secondaryButtonTestId: AddContactModalTestIds.CancelButton(),
            onPrimaryButtonClick: methods.handleSubmit(onValid),
            onSecondaryButtonClick: dismissModal,
            onCloseButtonClick: dismissModal,
            content: /*#__PURE__*/ _jsxs(Column, {
                gap: 24,
                children: [
                    /*#__PURE__*/ _jsx(ContactForm, {
                        namePrefix: CONTACT_FIELD_NAME,
                        title: t.contactTitle(),
                        accountingMapping: hasLinkedAccounting ? /*#__PURE__*/ _jsx(ContactAccountingMapping, {
                            namePrefix: CONTACT_FIELD_NAME
                        }) : undefined,
                        testId: CONTACT_FORM_TEST_ID
                    }),
                    hasError && /*#__PURE__*/ _jsx(Box, {
                        testId: AddContactModalTestIds.CreateContactError(),
                        children: /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            skin: "error",
                            children: t.createContactError()
                        })
                    })
                ]
            })
        }))
    }));
}
